'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { LogoutLink } from '@/shared/components/logout-link';
import { COPY } from '@/shared/constants/copies';
import { MIXPANEL_APP_FEATURES, mixpanelEvents } from '@/shared/constants/mixpanel-events';
import { emitMixpanelEvent } from '@/shared/utils/mixpanel-utils';

import { getApiErrorDetails } from '@/utils/axios';

import { useUserOnboardingSafe } from '../../contexts/user-onboarding-provider';
import { useNameCapture } from '../../hooks/use-name-capture';

/**
 * The single onboarding interruption: shown only when the backend could not detect
 * a name. Two hosts: the /ai gate (no provider — the gate refetches and swaps on
 * success) and the legacy anonymous-signup modal (provider present — advance the
 * screen flow on success and honor skipLayout, since the dialog owns the header).
 */
export function UserNameScreen() {
	const inputRef = useRef<HTMLInputElement>(null);
	const { validate, submit } = useNameCapture();
	const onboarding = useUserOnboardingSafe();

	const [name, setName] = useState('');
	const [error, setError] = useState('');
	const [isProvisioning, setIsProvisioning] = useState(false);

	useEffect(() => {
		inputRef.current?.focus();
		emitMixpanelEvent(mixpanelEvents.VIEWED_ONBOARDING_NAME_SCREEN, {
			feature: MIXPANEL_APP_FEATURES.USER_ONBOARDING,
		});
	}, []);

	const handleSubmit = useCallback(
		async (e: React.FormEvent<HTMLFormElement>) => {
			e.preventDefault();
			if (isProvisioning) return;

			const validation = validate(name);
			if (!validation.ok) {
				setError(validation.error);
				return;
			}

			setError('');
			setIsProvisioning(true);
			try {
				// Blocks while the backend creates + defaults the workspace and runs ATS
				// detection, then refetches userDetails. The gate swaps to the agent on success.
				await submit(name);
				// Modal host: the gate isn't watching, so advance the flow ourselves.
				if (onboarding) {
					onboarding.setUserName(name.trim());
					onboarding.goToNextScreen();
					setIsProvisioning(false);
				}
			} catch (err) {
				setError(getApiErrorDetails(err).errorMessage);
				setIsProvisioning(false);
			}
		},
		[isProvisioning, name, onboarding, submit, validate]
	);

	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			if (isProvisioning) return;
			setName(e.target.value);
			setError('');
		},
		[isProvisioning]
	);

	const form = (
		<form
			onSubmit={handleSubmit}
			className="flex w-full max-w-md flex-col gap-6"
		>
			{!onboarding?.skipLayout && (
				<div className="flex flex-col gap-2">
					<h1 className="text-2xl font-semibold tracking-tight text-slate-900">
						{COPY.onboarding.nameTitle}
					</h1>
					<p className="text-sm text-slate-500">{COPY.onboarding.nameScreenHelp}</p>
				</div>
			)}

			<div className="flex flex-col gap-2">
				<Label
					htmlFor="user-name"
					className="text-sm font-medium text-slate-700"
				>
					Full name
				</Label>
				<Input
					ref={inputRef}
					id="user-name"
					name="name"
					type="text"
					placeholder="Type here..."
					value={name}
					onChange={handleChange}
					disabled={isProvisioning}
					autoComplete="name"
				/>
				{error && <p className="text-sm font-light text-red-500">{error}</p>}
			</div>

			<Button
				type="submit"
				disabled={isProvisioning}
				className="h-auto w-full py-3"
			>
				{isProvisioning
					? onboarding
						? 'Saving…'
						: 'Setting up your workspace…'
					: 'Continue'}
			</Button>
		</form>
	);

	// The modal host renders its own dialog header/frame around the flow.
	if (onboarding?.skipLayout) return form;

	return (
		<div className="relative flex h-full w-full items-center justify-center overflow-hidden bg-slate-50 px-6 antialiased">
			<div className="absolute right-6 top-6">
				<LogoutLink />
			</div>
			{form}
		</div>
	);
}
